// src/pages/analytics/SalesVsPaymentsTab.tsx
//
// Sales vs Payments — sales billed vs payments received per period
// (recipe: sales-vs-payments). Bucket pills (week/month/quarter) + months
// window + calendar toggle (default from tenant config
// `analytics.default_calendar`, same pattern as YoYOverviewTab) + dimension
// select for the whole-window breakdown table.
//
// Period labels are server-formatted (Hebrew-calendar aware) — rendered
// as-is. diff = sales - payments: a POSITIVE gap means billing outran
// collections, so it renders red.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle } from 'lucide-react';
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useAuthContext } from '../../hooks/useAuth';
import { adminApi } from '../../services/adminApi';
import { apiV2 as api } from '../../services/apiV2';
import type { SvpPeriod, SvpDimensionRow, SalesVsPaymentsResponse } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const BUCKETS: { value: string; label: string }[] = [
  { value: 'week',    label: 'Week' },
  { value: 'month',   label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
];

const CALENDARS: { value: string; label: string }[] = [
  { value: 'gregorian', label: 'Gregorian' },
  { value: 'hebrew',    label: 'Hebrew' },
];

const DIMENSIONS: { value: string; label: string }[] = [
  { value: 'customer_category', label: 'Customer Category' },
  { value: 'salesman',          label: 'Salesman' },
  { value: 'area',              label: 'Area' },
];

const MONTHS_OPTIONS = [6, 12, 24];

function exportAll(data: SalesVsPaymentsResponse, dimensionLabel: string, exporter: typeof exportCsv = exportCsv) {
  const periodCols: CsvColumns<SvpPeriod> = [
    ['Period',   (r) => r.label],
    ['Sales',    (r) => r.sales],
    ['Payments', (r) => r.payments],
    ['Diff',     (r) => r.diff],
  ];
  exporter('sales-vs-payments-periods', periodCols, data.periods);
  const dimCols: CsvColumns<SvpDimensionRow> = [
    [dimensionLabel, (r) => r.label],
    ['Sales',        (r) => r.sales],
    ['Payments',     (r) => r.payments],
    ['Diff',         (r) => r.diff],
  ];
  exporter('sales-vs-payments-breakdown', dimCols, data.byDimension);
}

function DiffCell({ diff }: { diff: number }) {
  // diff = sales - payments; positive = payments lagging billing → red.
  return (
    <span className={`tabular-nums font-medium ${diff > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
      {fmtCur(diff)}
    </span>
  );
}

export function SalesVsPaymentsTab() {
  const { claims, activeTenant } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;

  const [bucket, setBucket] = useState('month');
  const [months, setMonths] = useState(12);
  const [dimension, setDimension] = useState('customer_category');
  // null = "no user choice yet" — fall through to the tenant config default.
  const [calendarOverride, setCalendarOverride] = useState<string | null>(null);
  const resetFilters = () => {
    setBucket('month');
    setMonths(12);
    setDimension('customer_category');
    setCalendarOverride(null);
  };

  const calendarDefaultQ = useQuery({
    queryKey: ['analyticsSvp', 'default-calendar', slug],
    queryFn: () => adminApi.getConfig('analytics.default_calendar', slug ?? undefined),
    enabled: !!slug,
    staleTime: 60 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
  const configCalendar = calendarDefaultQ.data?.value === 'hebrew' ? 'hebrew' : 'gregorian';
  const calendar = calendarOverride ?? configCalendar;

  const q = useQuery({
    queryKey: ['analyticsSvp', bucket, months, calendar, dimension],
    queryFn: () => api.getSalesVsPayments({ bucket, months, calendar, dimension }),
    // Wait for the tenant default so we don't fetch gregorian and then
    // immediately refetch hebrew on tenants configured for it.
    enabled: !slug || calendarOverride !== null || !calendarDefaultQ.isLoading,
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const periods = data?.periods ?? [];
  const byDimension = data?.byDimension ?? [];
  const totals = data?.totals;
  const gap = totals ? totals.sales - totals.payments : 0;
  const dimensionLabel = DIMENSIONS.find(d => d.value === dimension)?.label ?? dimension;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <ArrowLeftRight size={20} /> Sales vs Payments
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Sales billed vs payments received per period
          {data && <span className="text-slate-500"> · {data.bucket} buckets · {data.calendar} calendar</span>}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Bucket</span>
          <div className="flex items-center gap-1.5">
            {BUCKETS.map(b => (
              <button key={b.value} onClick={() => setBucket(b.value)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  bucket === b.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {b.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Window</span>
          <select value={months} onChange={e => setMonths(Number(e.target.value))}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {MONTHS_OPTIONS.map(m => <option key={m} value={m}>{m} months</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Calendar</span>
          <div className="flex items-center gap-1.5">
            {CALENDARS.map(c => (
              <button key={c.value} onClick={() => setCalendarOverride(c.value)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  calendar === c.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Breakdown</span>
          <select value={dimension} onChange={e => setDimension(e.target.value)}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {DIMENSIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div className="flex-1" />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => data && exportAll(data, dimensionLabel)}
          disabled={!data || periods.length === 0}
          title="Export periods + breakdown to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => data && exportAll(data, dimensionLabel, exportXlsx)}
          disabled={!data || periods.length === 0}
          title="Export periods + breakdown to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load sales vs payments</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading || (!q.data && !q.error) ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <>
          {/* Totals tiles */}
          {totals && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4">
                <div className="text-xs text-slate-400 uppercase tracking-wider">Sales</div>
                <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtCur(totals.sales)}</div>
              </div>
              <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4">
                <div className="text-xs text-slate-400 uppercase tracking-wider">Payments</div>
                <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtCur(totals.payments)}</div>
              </div>
              <div className={`rounded-xl border p-4 ${gap > 0 ? 'border-red-500/30 bg-red-500/5' : 'border-emerald-500/30 bg-emerald-500/5'}`}>
                <div className="text-xs text-slate-400 uppercase tracking-wider">Gap (Sales − Payments)</div>
                <div className={`text-xl font-semibold tabular-nums mt-1 ${gap > 0 ? 'text-red-300' : 'text-emerald-300'}`}>{fmtCur(gap)}</div>
              </div>
            </div>
          )}

          {/* Grouped bars per period */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Sales &amp; payments by {data.bucket}</h3>
            {periods.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No periods to chart</div>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={periods} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      interval="preserveStartEnd" minTickGap={16} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip
                      cursor={{ fill: '#475569', fillOpacity: 0.15 }}
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                      formatter={(value, name) => [fmtCur(Number(value)), name === 'sales' ? 'Sales' : 'Payments']}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }}
                      formatter={(v) => <span style={{ color: '#94a3b8' }}>{v === 'sales' ? 'Sales' : 'Payments'}</span>} />
                    <Bar dataKey="sales" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="payments" fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Periods table */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[140px]">Period</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Sales</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Payments</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((p) => (
                    <tr key={p.key} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                      <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap">{p.label}</td>
                      <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(p.sales)}</td>
                      <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur(p.payments)}</td>
                      <td className="px-3 py-2 text-right"><DiffCell diff={p.diff} /></td>
                    </tr>
                  ))}
                </tbody>
                {totals && periods.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                      <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                      <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.sales)}</td>
                      <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(totals.payments)}</td>
                      <td className="px-3 py-2 text-right"><DiffCell diff={totals.diff} /></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {periods.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">No rows to show</div>
            )}
          </div>

          {/* Whole-window breakdown by dimension */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="px-3 py-2.5 border-b border-slate-700/40 text-sm font-semibold text-white">
              Breakdown by {dimensionLabel} — whole window
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">{dimensionLabel}</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Sales</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Payments</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {byDimension.map((r, i) => (
                    <tr key={`${r.label}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                      <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[260px] truncate" title={r.label}>{r.label}</td>
                      <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.sales)}</td>
                      <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur(r.payments)}</td>
                      <td className="px-3 py-2 text-right"><DiffCell diff={r.diff} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {byDimension.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">No rows to show</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
